import $ from "jquery";
import LettersTile from "./lettersTile";

interface TileConfig {
  id: string;
  letter: string;
  isEmpty?: boolean;
}

interface DragState {
  tile: LettersTile;
  pointerId: number;
  startedAt: number;
  startRect: DOMRect;
  offset: { x: number; y: number };
  visualStartRect?: DOMRect;
  originalTransform: string;
  startPointer: { x: number; y: number };
  moved: boolean;
  originalBoardSquare?: HTMLElement;
}

function isJQueryElement(element: any): element is any {
  return Boolean(element?.jquery);
}

export default class TileManager {
  private readonly $container: any;
  private readonly tiles: LettersTile[] = [];
  private readonly tileLookup = new Map<HTMLElement, LettersTile>();
  private readonly tileIdLookup = new Map<string, LettersTile>();
  private dragState: DragState | null = null;
  private dropTarget: LettersTile | null = null;
  private suppressClickTarget: HTMLElement | null = null;
  private lastTouchTap: { tileId: string; time: number } | null = null;

  private readonly handlePointerDown = (event: PointerEvent) => {
    const tile = this.getTileFromElement(event.currentTarget as HTMLElement);
    if (!tile || tile.isEmptyTile()) {
      this.lastTouchTap = null;
      return;
    }
    event.preventDefault();

    this.setDropTarget(null);
    this.suppressClickTarget = null;

    const element = tile.getElement();
    // Get the current visual position (this is where the tile actually is)
    const visualRect = tile.getBoundingClientRect();

    // Get the natural DOM position (temporarily remove transform)
    const currentTransform = element.style.transform;
    element.style.transform = "none";
    const domRect = element.getBoundingClientRect();
    element.style.transform = currentTransform;

    const offset = {
      x: event.clientX - visualRect.left,
      y: event.clientY - visualRect.top,
    };

    // Find if this tile is currently on a board square

    const originalBoardSquare = this.findBoardSquareWithTile(tile.getId());

    this.dragState = {
      tile,
      pointerId: event.pointerId,
      startedAt: event.timeStamp,
      startRect: domRect, // Use DOM position for transform calculations
      offset,
      originalBoardSquare,
      originalTransform: currentTransform,
      startPointer: { x: event.clientX, y: event.clientY },
      moved: false,
    };

    // Store the visual position for drag calculations
    this.dragState.visualStartRect = visualRect;

    tile.setActive(true);
    tile.setDragging(true);
    tile.setHovered(false);
    tile.setFocused(false);
    tile.setZIndex(1000);
    // Don't change position, tile stays where it is visually

    try {
      element.setPointerCapture(event.pointerId);
    } catch (error) {
      // Ignore pointer capture errors (e.g., Safari).
    }

    window.addEventListener("pointermove", this.handlePointerMove, {
      passive: false,
    });
    window.addEventListener("pointerup", this.handlePointerUp, {
      passive: false,
    });
    window.addEventListener("pointercancel", this.handlePointerUp, {
      passive: false,
    });
  };

  private readonly handlePointerEnter = (event: PointerEvent) => {
    const tile = this.getTileFromElement(event.currentTarget as HTMLElement);
    if (!tile || tile.isEmptyTile() || this.dragState?.tile === tile) {
      return;
    }
    tile.setHovered(true);
  };

  private readonly handlePointerLeave = (event: PointerEvent) => {
    const tile = this.getTileFromElement(event.currentTarget as HTMLElement);
    if (!tile || tile.isEmptyTile()) {
      return;
    }
    tile.setHovered(false);
  };

  private readonly handlePointerMove = (event: PointerEvent) => {
    if (event.pointerId !== this.dragState?.pointerId) {
      return;
    }

    event.preventDefault();
    if (
      Math.hypot(
        event.clientX - this.dragState.startPointer.x,
        event.clientY - this.dragState.startPointer.y,
      ) > 4
    )
      this.dragState.moved = true;
    if (!this.dragState.moved) return;
    const { tile, startRect, offset } = this.dragState;
    const targetX = event.clientX - offset.x;
    const targetY = event.clientY - offset.y - 10;

    // Calculate where the mouse wants the tile to be, relative to DOM position
    const dx = targetX - startRect.left;
    const dy = targetY - startRect.top;

    tile.setTransform(`translate3d(${dx}px, ${dy}px, 0)`);

    const candidate = this.findTileAtPoint(event.clientX, event.clientY);
    const nextDropTarget =
      candidate && candidate !== tile && candidate.isEmptyTile()
        ? candidate
        : null;
    this.setDropTarget(nextDropTarget);
  };

  private readonly handlePointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.dragState?.pointerId) {
      return;
    }

    event.preventDefault();
    const { tile } = this.dragState;
    const element = tile.getElement();

    try {
      element.releasePointerCapture(event.pointerId);
    } catch (error) {
      // Ignore pointer release errors.
    }

    window.removeEventListener("pointermove", this.handlePointerMove);
    window.removeEventListener("pointerup", this.handlePointerUp);
    window.removeEventListener("pointercancel", this.handlePointerUp);

    if (!this.dragState.moved || event.type === "pointercancel") {
      const isPlacedTileTap =
        event.type === "pointerup" &&
        event.pointerType === "touch" &&
        !this.dragState.moved &&
        event.timeStamp - this.dragState.startedAt < 350 &&
        this.dragState.originalBoardSquare;
      tile.setTransform(this.dragState.originalTransform);
      tile.setZIndex(this.dragState.originalBoardSquare ? 1000 : null);
      tile.setDragging(false);
      tile.setActive(false);
      this.setDropTarget(null);
      this.dragState = null;

      const previousTap = this.lastTouchTap;
      this.lastTouchTap = isPlacedTileTap
        ? { tileId: tile.getId(), time: event.timeStamp }
        : null;
      if (
        isPlacedTileTap &&
        previousTap?.tileId === tile.getId() &&
        event.timeStamp - previousTap.time < 350
      ) {
        this.lastTouchTap = null;
        // The following compatibility click must not put the returned tile back.
        this.suppressClickTarget = element;
        element.dispatchEvent(
          new CustomEvent("wordspider:tile-double-tap", { bubbles: true }),
        );
      }
      return;
    }
    this.lastTouchTap = null;
    this.suppressClickTarget = element;
    const dropTarget = this.dropTarget;
    if (dropTarget && dropTarget !== tile && dropTarget.isEmptyTile()) {
      // Check if the target square already has a letter dropped on it
      const targetElement = dropTarget.getElement();
      const existingLetter = targetElement.getAttribute("data-letter");

      if (existingLetter && existingLetter.trim() !== "") {
        // Target square already has a letter, reject the drop

        tile.setTransform(this.dragState.originalTransform);
        tile.setZIndex(this.dragState.originalBoardSquare ? 1000 : null);
      } else {
        // Valid drop - move tile to the visual position of the target empty tile
        const targetRect = dropTarget.getBoundingClientRect();

        // Reset transform first to get the tile's natural DOM position
        tile.resetTransform();
        const tileDOMRect = tile.getElement().getBoundingClientRect();

        // Calculate offset from DOM position to target position
        const tileCenterX = tileDOMRect.left + tileDOMRect.width / 2;
        const tileCenterY = tileDOMRect.top + tileDOMRect.height / 2;
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const targetCenterY = targetRect.top + targetRect.height / 2;
        const dx = targetCenterX - tileCenterX;
        const dy = targetCenterY - tileCenterY;

        // Set transform to move tile to target position
        tile.setTransform(`translate3d(${dx}px, ${dy}px, 0)`);

        // Clear the original board square if tile was moved from another square
        if (this.dragState.originalBoardSquare) {
          this.dragState.originalBoardSquare.setAttribute("data-letter", "");
        }

        // Store the dropped letter tile's ID on the target empty square
        const droppedTileId = tile.getId();
        targetElement.setAttribute("data-letter", droppedTileId);

        targetElement.dispatchEvent(
          new CustomEvent("wordspider:tile-drop", {
            detail: {
              tileId: droppedTileId,
              row: Number(targetElement.dataset.row ?? "0"),
              col: Number(targetElement.dataset.col ?? "0"),
            },
            bubbles: true,
          }),
        );
      }

      // Keep elevated z-index so tile stays on top
      // tile.setZIndex(1000) - keep the elevated z-index
    } else {
      // No valid drop target - reset position and z-index

      // Clear the original board square since tile is going back to letters container
      if (this.dragState.originalBoardSquare) {
        this.dragState.originalBoardSquare.setAttribute("data-letter", "");
      }

      tile.resetTransform();
      tile.setZIndex(null);
    }

    tile.setDragging(false);
    tile.setActive(false);

    this.setDropTarget(null);
    this.dragState = null;
  };

  constructor(container: any) {
    this.$container = isJQueryElement(container) ? container : $(container);
    if (!this.$container.length) {
      throw new Error("TileManager: container element not found.");
    }
  }

  public clear(): void {
    window.removeEventListener("pointermove", this.handlePointerMove);
    window.removeEventListener("pointerup", this.handlePointerUp);
    window.removeEventListener("pointercancel", this.handlePointerUp);
    this.dragState = null;
    this.lastTouchTap = null;
    this.setDropTarget(null);
    for (const tile of this.tiles) tile.destroy();
    this.tiles.length = 0;
    this.tileLookup.clear();
    this.tileIdLookup.clear();
  }

  public getContainer(): any {
    return this.$container;
  }

  public createTile(config: TileConfig): LettersTile {
    const tile = new LettersTile(
      this,
      config.id,
      config.letter,
      config.isEmpty ?? false,
    );
    this.tiles.push(tile);
    this.tileLookup.set(tile.getElement(), tile);
    if (config.id) {
      this.tileIdLookup.set(config.id, tile);
    }
    this.attachTileEvents(tile);
    return tile;
  }

  public getTileById(id: string): LettersTile | null {
    if (!id) {
      return null;
    }
    return this.tileIdLookup.get(id) ?? null;
  }

  private attachTileEvents(tile: LettersTile): void {
    const element = tile.getElement();
    element.addEventListener("pointerdown", this.handlePointerDown, {
      passive: false,
    });
    element.addEventListener(
      "click",
      (event) => {
        if (this.suppressClickTarget === element) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.suppressClickTarget = null;
        }
      },
      { capture: true },
    );
    element.addEventListener("pointerenter", this.handlePointerEnter);
    element.addEventListener("pointerleave", this.handlePointerLeave);
  }

  private getTileFromElement(element: HTMLElement | null): LettersTile | null {
    if (!element) {
      return null;
    }
    if (this.tileLookup.has(element)) {
      return this.tileLookup.get(element) ?? null;
    }
    const candidate = element.closest<HTMLElement>("[data-letter-id]");
    if (!candidate) {
      return null;
    }
    return this.tileLookup.get(candidate) ?? null;
  }

  private findTileAtPoint(x: number, y: number): LettersTile | null {
    const elements =
      typeof document.elementsFromPoint === "function"
        ? document.elementsFromPoint(x, y)
        : [document.elementFromPoint(x, y)];

    for (const node of elements) {
      if (!node) {
        continue;
      }
      const candidate = this.getTileFromElement(node as HTMLElement);
      if (!candidate) {
        continue;
      }
      if (candidate === this.dragState?.tile) {
        continue;
      }
      return candidate;
    }

    return null;
  }

  private setDropTarget(tile: LettersTile | null): void {
    if (this.dropTarget === tile) {
      return;
    }
    if (this.dropTarget) {
      this.dropTarget.setDropTargetState(false);
    }
    this.dropTarget = tile;
    if (tile) {
      tile.setDropTargetState(true);
    }
  }

  private findBoardSquareWithTile(tileId: string): HTMLElement | null {
    // Look for any board square that has this tile's ID in its data-letter attribute
    const boardSquares = document.querySelectorAll('[id^="board-"]');

    for (let i = 0; i < boardSquares.length; i++) {
      const square = boardSquares[i];
      const dataLetter = square.getAttribute("data-letter");
      if (dataLetter === tileId) {
        return square as HTMLElement;
      }
    }

    return null;
  }

  private moveTileBefore(moving: LettersTile, target: LettersTile): void {
    const containerEl = this.$container[0];
    if (!containerEl) {
      return;
    }
    const movingEl = moving.getElement();
    const targetEl = target.getElement();
    if (movingEl === targetEl) {
      return;
    }

    containerEl.insertBefore(movingEl, targetEl);

    const currentIndex = this.tiles.indexOf(moving);
    if (currentIndex >= 0) {
      this.tiles.splice(currentIndex, 1);
    }
    const targetIndex = this.tiles.indexOf(target);
    if (targetIndex >= 0) {
      this.tiles.splice(targetIndex, 0, moving);
    } else {
      this.tiles.push(moving);
    }
  }
}
